import { useQueryClient } from "@tanstack/react-query";
import { transactionsDetailKeys } from "Api/Queries/Transactions/useTransactionsDetail";
import { transactionStatusKeys } from "Api/Queries/Transactions/useTransactionStatus";
import {
  type SignalRTransactionStatusUpdate,
  signalRService,
} from "Infrastructure/SignalR/SignalRService";
import { useEffect } from "react";

type Props = {
  publicID?: string;
  enabled?: boolean;
};

/**
 * Listens for SignalR transaction status updates and updates React Query cache.
 * Used alongside useTransactionStatus to provide real-time updates with polling fallback.
 */
export const useTransactionStatusSignalR = ({
  publicID,
  enabled = true,
}: Props) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!publicID || !enabled) {
      return;
    }

    const handleStatusUpdate = (update: SignalRTransactionStatusUpdate) => {
      // Only handle updates for the transaction we're watching
      if (update.publicID !== publicID) {
        return;
      }

      // Invalidate the query to trigger a fresh fetch
      // This ensures we have the complete transaction data, not just the status
      queryClient.invalidateQueries({
        queryKey: transactionStatusKeys.byPublicId(publicID),
      });
      // ZS-1676: a refund booking pushes this event WITHOUT a status change (the original stays
      // Success) — the refund state lives in the detail DTO, so the detail cache must refetch too.
      queryClient.invalidateQueries({
        queryKey: transactionsDetailKeys.all,
      });
    };

    // Try to subscribe immediately
    const subscribed =
      signalRService.onReceiveTransactionStatusUpdate(handleStatusUpdate);

    // If subscription failed (connection not ready), retry when connection becomes ready
    const cleanupConnectionReady = subscribed
      ? () => {} // No-op if already subscribed
      : signalRService.onConnectionReady(() => {
          signalRService.onReceiveTransactionStatusUpdate(handleStatusUpdate);
        });

    // Cleanup: unsubscribe on unmount and remove connection ready callback
    return () => {
      signalRService.offReceiveTransactionStatusUpdate(handleStatusUpdate);
      cleanupConnectionReady();
    };
  }, [publicID, enabled, queryClient]);
};
